import { io, Socket } from 'socket.io-client';

// Socket event types
export interface ServerToClientEvents {
  // Lobby events
  'lobby:member_joined': (data: LobbyMemberEvent) => void;
  'lobby:member_left': (data: LobbyMemberEvent) => void;
  'lobby:wager_created': (data: LobbyWagerEvent) => void;
  'lobby:presence_update': (data: PresenceUpdateEvent) => void;

  // Challenge events
  'challenge:received': (data: ChallengeEvent) => void;
  'challenge:accepted': (data: ChallengeEvent) => void;
  'challenge:declined': (data: ChallengeEvent) => void;
  'challenge:expired': (data: ChallengeEvent) => void;

  // Wager events
  'wager:status_changed': (data: WagerStatusEvent) => void;
  'wager:updated': (data: { wager: Record<string, unknown> }) => void;
  'wager:matched': (data: { wager: Record<string, unknown> }) => void;
  'wager:opponent_joined': (data: WagerOpponentEvent) => void;
  'wager:funded': (data: WagerFundedEvent) => void;
  'wager:match_result': (data: WagerMatchResultEvent) => void;
  'wager:settled': (data: WagerSettledEvent) => void;

  // Message events
  'message:received': (data: { message: { id: string; senderId: string; senderName: string; content: string; type?: string; metadata?: Record<string, unknown> | null; createdAt: string } }) => void;

  // Friend events
  'friend:request': (data: { friendship: { id: string; requester: { id: string; displayName: string; avatarUrl: string | null } } }) => void;
  'friend:accepted': (data: { friendship: { id: string; friend: { id: string; displayName: string; avatarUrl: string | null } } }) => void;

  // Notification events
  'notification': (data: NotificationEvent) => void;
  'notification:new': (data: NotificationEvent) => void;
  'notification:count': (data: NotificationCountEvent) => void;

  // Connection events
  'connect': () => void;
  'disconnect': (reason: string) => void;
  'connect_error': (error: Error) => void;
}

export interface ClientToServerEvents {
  // Lobby events
  'lobby:join': (data: { gameCode: string }) => void;
  'lobby:leave': (data: { gameCode: string }) => void;
  'lobby:set_presence': (data: { status: PresenceStatus }) => void;

  // Challenge events
  'challenge:send': (data: { targetUserId: string; wagerId: string }) => void;
  'challenge:respond': (data: { challengeId: string; accept: boolean }) => void;

  // Wager events
  'wager:subscribe': (data: { wagerId: string }) => void;
  'wager:unsubscribe': (data: { wagerId: string }) => void;

  // Auth
  'auth:authenticate': (data: { token: string }) => void;
}

// Event data types
export interface LobbyMemberEvent {
  gameCode: string;
  userId: string;
  displayName: string;
  avatarUrl?: string;
  gameUsername?: string;
  rating?: number;
  status: PresenceStatus;
}

export interface LobbyWagerEvent {
  gameCode: string;
  wagerId: string;
  wagerType: string;
  amount: string;
  currency: string;
  creatorId: string;
  creatorName: string;
}

export interface PresenceUpdateEvent {
  userId: string;
  status: PresenceStatus;
  gameCode?: string;
}

export type PresenceStatus = 'online' | 'away' | 'in_match' | 'offline';

export interface ChallengeEvent {
  challengeId: string;
  wagerId: string;
  challengerId: string;
  challengerName: string;
  challengerAvatar?: string;
  targetId: string;
  gameCode: string;
  amount: string;
  currency: string;
  expiresAt: string;
}

export interface WagerStatusEvent {
  wagerId: string;
  previousStatus: string;
  newStatus: string;
  updatedAt: string;
}

export interface WagerOpponentEvent {
  wagerId: string;
  opponentId: string;
  opponentName: string;
  opponentAvatar?: string;
}

export interface WagerFundedEvent {
  wagerId: string;
  userId: string;
  txHash: string;
  fullyFunded: boolean;
}

export interface WagerMatchResultEvent {
  wagerId: string;
  winnerId: string | null;
  matchId: string;
  verified: boolean;
}

export interface WagerSettledEvent {
  wagerId: string;
  winnerId: string;
  amount: string;
  txHash: string;
}

export interface NotificationEvent {
  id: string;
  type: string;
  title: string;
  body?: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationCountEvent {
  unreadCount: number;
}

// Typed socket
export type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Socket instance (lazily created)
let socket: TypedSocket | null = null;

// Configuration
// Default to API server origin for WebSocket (Vercel doesn't support WS)
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ||
  (import.meta.env.VITE_API_URL ? new URL(import.meta.env.VITE_API_URL).origin : '');

export interface SocketConfig {
  token: string;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Initialize socket connection with authentication
 */
export function initializeSocket(config: SocketConfig): TypedSocket {
  if (socket?.connected) {
    return socket;
  }

  // Disconnect existing socket if any
  if (socket) {
    socket.disconnect();
  }

  socket = io(SOCKET_URL, {
    auth: {
      token: config.token,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  });

  // Connection event handlers
  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket?.id);
    config.onConnect?.();
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] Disconnected:', reason);
    config.onDisconnect?.(reason);
  });

  socket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error.message);
    config.onError?.(error);
  });

  return socket;
}

/**
 * Get the current socket instance
 */
export function getSocket(): TypedSocket | null {
  return socket;
}

/**
 * Disconnect and cleanup socket
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

/**
 * Check if socket is connected
 */
export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Emit event with type safety
 */
export function emitEvent<K extends keyof ClientToServerEvents>(
  event: K,
  ...args: Parameters<ClientToServerEvents[K]>
): void {
  if (!socket?.connected) {
    console.warn('[Socket] Cannot emit, socket not connected');
    return;
  }
  socket.emit(event, ...args);
}

/**
 * Join a game lobby
 */
export function joinLobby(gameCode: string): void {
  emitEvent('lobby:join', { gameCode });
}

/**
 * Leave a game lobby
 */
export function leaveLobby(gameCode: string): void {
  emitEvent('lobby:leave', { gameCode });
}

/**
 * Subscribe to wager updates
 */
export function subscribeToWager(wagerId: string): void {
  emitEvent('wager:subscribe', { wagerId });
}

/**
 * Unsubscribe from wager updates
 */
export function unsubscribeFromWager(wagerId: string): void {
  emitEvent('wager:unsubscribe', { wagerId });
}

/**
 * Update presence status
 */
export function setPresence(status: PresenceStatus): void {
  emitEvent('lobby:set_presence', { status });
}
